import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { useMedplum } from "@medplum/react";

import { PatientContext } from "@/contexts/patient";
import { LABS_PATIENT_REFRESH_TOAST } from "@/health-gorilla/constants/labs-refresh-messages";
import { toast } from "@/utils/notifications";

interface UseRefreshPatientForLabsBannerOptions {
  /** Refetch when the user returns to this browser tab (e.g. after editing profile elsewhere). */
  refreshOnTabFocus?: boolean;
  /** Refetch when the host page mounts (e.g. navigating to Results after editing Profile). */
  refreshOnMount?: boolean;
}

interface RefreshPatientOptions {
  showErrors?: boolean;
}

/**
 * Reloads the client Patient from Medplum and updates PatientContext so labs
 * readiness checks (e.g. required-fields banner) reflect the latest profile.
 */
export function useRefreshPatientForLabsBanner(
  options: UseRefreshPatientForLabsBannerOptions = {},
) {
  const medplum = useMedplum();
  const { patient, setPatient } = useContext(PatientContext);
  const patientId = patient?.id;
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isMountedRef = useRef(true);
  const isRefreshInFlightRef = useRef(false);
  const refreshPatientRef = useRef<(refreshOptions?: RefreshPatientOptions) => Promise<void>>(
    async () => undefined,
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const refreshPatient = useCallback(
    async (refreshOptions?: RefreshPatientOptions) => {
      const showErrors = refreshOptions?.showErrors ?? true;
      if (!patientId) {
        if (showErrors) {
          toast({
            type: "error",
            ...LABS_PATIENT_REFRESH_TOAST.missingClientId,
            autoClose: false,
          });
        }
        return;
      }

      if (isRefreshInFlightRef.current) {
        return;
      }

      isRefreshInFlightRef.current = true;
      if (isMountedRef.current) {
        setIsRefreshing(true);
      }

      try {
        medplum.invalidateSearches("Patient");
        const updatedPatient = await medplum.readResource("Patient", patientId, {
          cache: "reload",
        });
        if (isMountedRef.current) {
          setPatient(updatedPatient);
        }
      } catch (error) {
        if (!showErrors) {
          console.error("Background patient refresh for labs banner failed:", error);
          return;
        }
        console.error("Failed to refresh client for labs banner:", error);
        toast({
          type: "error",
          ...LABS_PATIENT_REFRESH_TOAST.reloadFailed,
          autoClose: false,
        });
      } finally {
        isRefreshInFlightRef.current = false;
        if (isMountedRef.current) {
          setIsRefreshing(false);
        }
      }
    },
    [medplum, patientId, setPatient],
  );

  refreshPatientRef.current = refreshPatient;

  useEffect(() => {
    if (!options.refreshOnMount || !patientId) {
      return;
    }
    void refreshPatientRef.current({ showErrors: false });
  }, [options.refreshOnMount, patientId]);

  useEffect(() => {
    if (!options.refreshOnTabFocus) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void refreshPatientRef.current({ showErrors: false });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [options.refreshOnTabFocus]);

  return { refreshPatient, isRefreshing };
}
